import { useEffect, useRef } from 'react';

/**
 * Login background — animated particle/links field, CANVAS-based.
 *
 * Faithful restore of the old tsparticles "links" preset (purple dots drifting
 * with connecting lines + hover-repulse), but implemented as a self-contained
 * <canvas> + requestAnimationFrame loop. The whole animation lives in refs —
 * NO React state updates per frame — so it physically cannot reproduce the
 * `@tsparticles/react` "maximum update depth exceeded" crash that took the
 * particles off the release-critical login path in the first place (see
 * routes/-login.no-particles.test.ts). It also:
 *   - scales to its container via ResizeObserver + devicePixelRatio (retina),
 *   - honours `prefers-reduced-motion` (renders one static frame, no loop),
 *   - fully tears down (rAF, observer, listeners) on unmount.
 *
 * Visual params mirror the original component (particles-background.tsx):
 * colors #8B5CF6/#A78BFA/#7C3AED/#94a3b8, link distance 150 @ 0.2 opacity,
 * drift speed ~1.2, per-particle opacity 0.3–0.7, size 1–3, hover repulse.
 */

const COLORS = ['#8B5CF6', '#A78BFA', '#7C3AED', '#94a3b8'];
const LINK_COLOR = '139, 92, 246'; // #8B5CF6 as rgb for rgba() links
const LINK_DISTANCE = 150;
const LINK_OPACITY = 0.2;
const DRIFT_SPEED = 1.2; // px/frame baseline (matches preset speed)
const REPULSE_DISTANCE = 180;
// One particle per ~9.6k device-independent px², capped so dense displays stay
// smooth (the O(n²) link pass is the cost driver).
const AREA_PER_PARTICLE = 9600;
const MAX_PARTICLES = 200;

interface P {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  o: number;
  color: string;
}

export function LoginParticles() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles: P[] = [];
    let raf = 0;
    const pointer = { x: -9999, y: -9999, active: false };

    const rand = (min: number, max: number) => min + Math.random() * (max - min);

    function makeParticles() {
      const target = Math.min(
        MAX_PARTICLES,
        Math.max(24, Math.round((width * height) / AREA_PER_PARTICLE)),
      );
      particles = Array.from({ length: target }, () => {
        const angle = rand(0, Math.PI * 2);
        return {
          x: rand(0, width),
          y: rand(0, height),
          vx: Math.cos(angle) * DRIFT_SPEED,
          vy: Math.sin(angle) * DRIFT_SPEED,
          r: rand(1, 3),
          o: rand(0.3, 0.7),
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
        };
      });
    }

    function resize() {
      const parent = canvas!.parentElement;
      width = parent?.clientWidth ?? window.innerWidth;
      height = parent?.clientHeight ?? window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width = Math.floor(width * dpr);
      canvas!.height = Math.floor(height * dpr);
      canvas!.style.width = `${width}px`;
      canvas!.style.height = `${height}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      makeParticles();
      if (reduced) draw(); // static single frame
    }

    function draw() {
      ctx!.clearRect(0, 0, width, height);

      // Links first (under the dots).
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < LINK_DISTANCE) {
            const alpha = LINK_OPACITY * (1 - dist / LINK_DISTANCE);
            ctx!.strokeStyle = `rgba(${LINK_COLOR}, ${alpha})`;
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.stroke();
          }
        }
      }

      // Dots.
      for (const p of particles) {
        ctx!.globalAlpha = p.o;
        ctx!.fillStyle = p.color;
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx!.fill();
      }
      ctx!.globalAlpha = 1;
    }

    function step() {
      for (const p of particles) {
        // Hover repulse — push particles away from the pointer.
        if (pointer.active) {
          const dx = p.x - pointer.x;
          const dy = p.y - pointer.y;
          const dist = Math.hypot(dx, dy);
          if (dist < REPULSE_DISTANCE && dist > 0.01) {
            const force = (1 - dist / REPULSE_DISTANCE) * 1.4;
            p.x += (dx / dist) * force;
            p.y += (dy / dist) * force;
          }
        }
        p.x += p.vx;
        p.y += p.vy;
        // outMode 'out' → wrap around the edges.
        if (p.x < -5) p.x = width + 5;
        else if (p.x > width + 5) p.x = -5;
        if (p.y < -5) p.y = height + 5;
        else if (p.y > height + 5) p.y = -5;
      }
      draw();
      raf = window.requestAnimationFrame(step);
    }

    const onMove = (e: PointerEvent) => {
      const rect = canvas!.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.active = true;
    };
    const onLeave = () => {
      pointer.active = false;
      pointer.x = -9999;
      pointer.y = -9999;
    };

    const ro = new ResizeObserver(resize);
    if (canvas.parentElement) ro.observe(canvas.parentElement);
    resize();

    if (!reduced) {
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerout', onLeave, { passive: true });
      raf = window.requestAnimationFrame(step);
    }

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerout', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="login-particles"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
